import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CodeIcon from '@mui/icons-material/Code';
import CloudIcon from '@mui/icons-material/Cloud';
import StorageIcon from '@mui/icons-material/Storage';
import DataObjectIcon from '@mui/icons-material/DataObject';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import SpeedIcon from '@mui/icons-material/Speed';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { Link } from 'react-router-dom';

const SoftwareDevelopment: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              Custom Software Development
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Tailored software solutions that drive innovation and business growth
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Enterprise-Grade Software Solutions
              </Typography>
              <Typography paragraph>
                At Right Track Technologies Limited, we specialize in building custom software solutions that address unique business challenges.
                Our expert development team combines technical expertise with industry knowledge to deliver powerful, scalable, and secure software
                that drives your business forward.
              </Typography>
              <Typography paragraph>
                Whether you need a complex enterprise system, web application, middleware solution, or integration platform, 
                we deliver software that enhances productivity, streamlines operations, and creates competitive advantages for your organization.
              </Typography>
              <Typography paragraph>
                We follow industry best practices and employ modern software architectures to ensure that your solution is not only powerful 
                today but remains flexible and adaptable for tomorrow's challenges and opportunities.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1573495627361-d9b87960b12d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Software Development"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Services Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Our Software Development Services
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              We offer comprehensive software development services to help organizations at every stage of their software journey.
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 4,
            justifyContent: 'center'
          }}>
            {[
              {
                title: "Custom Application Development",
                description: "End-to-end development of custom applications designed to address specific business needs and challenges.",
                icon: <CodeIcon fontSize="large" />,
              },
              {
                title: "Cloud Solutions",
                description: "Cloud-native application development, cloud migration, and optimization services for AWS, Azure, and Google Cloud.",
                icon: <CloudIcon fontSize="large" />,
              },
              {
                title: "Database Design & Development",
                description: "Designing efficient, scalable database structures and implementing robust data management solutions.",
                icon: <StorageIcon fontSize="large" />,
              },
              {
                title: "API Development & Integration",
                description: "Creating and implementing APIs to connect systems, services, and data sources for seamless information flow.",
                icon: <DataObjectIcon fontSize="large" />,
              },
              {
                title: "Legacy System Modernization",
                description: "Transforming outdated systems into modern, scalable solutions while preserving business logic and data integrity.",
                icon: <SettingsSuggestIcon fontSize="large" />,
              },
              {
                title: "DevOps Implementation",
                description: "Establishing efficient CI/CD pipelines and DevOps practices to accelerate development and deployment cycles.",
                icon: <AutoGraphIcon fontSize="large" />,
              },
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ width: isMobile ? '100%' : 'calc(33.333% - 16px)' }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box sx={{ color: '#0c2340', mb: 2 }}>
                    {service.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                    {service.title}
                  </Typography>
                  <Typography color="text.secondary" sx={{ flex: 1 }}>
                    {service.description}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Development Process Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
          >
            Our Development Process
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 6 
            }}
          >
            We follow a structured, transparent development methodology to ensure successful project delivery.
          </Typography>
        </motion.div>

        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 3,
            mb: 8
          }}
        >
          {[
            {
              step: "1",
              title: "Discovery & Planning",
              description: "We begin by thoroughly understanding your business needs, gathering requirements, and defining project scope and objectives.",
            },
            {
              step: "2",
              title: "Architecture & Design",
              description: "Our architects design a robust, scalable solution architecture and create detailed specifications for the development team.",
            },
            {
              step: "3",
              title: "Agile Development",
              description: "We develop your solution in iterative sprints, ensuring regular deliverables and flexibility to adapt to changing requirements.",
            },
            {
              step: "4",
              title: "Testing & QA",
              description: "Comprehensive testing to ensure functionality, performance, security, and usability meet the defined standards.",
            },
            {
              step: "5",
              title: "Deployment & Support",
              description: "Smooth deployment to production, followed by ongoing maintenance and support to ensure optimal performance.",
            },
          ].map((phase, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              style={{ flex: 1 }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: '#0c2340',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                  }}
                >
                  {phase.step}
                </Box>
                <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                  {phase.title}
                </Typography>
                <Typography color="text.secondary">
                  {phase.description}
                </Typography>
                
                {/* Connector line */}
                {index < 4 && !isMobile && (
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: 30,
                      right: -16,
                      width: 32,
                      height: 2,
                      bgcolor: '#0c2340',
                      zIndex: 0
                    }} 
                  />
                )}
              </Box>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* Technologies Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Our Technology Expertise
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              We leverage the best technologies to deliver robust, scalable software solutions.
            </Typography>
          </motion.div>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
            {[
              "Java", "C#", ".NET Core", "Python", "JavaScript", "TypeScript",
              "React", "Angular", "Node.js", "Spring Boot", "AWS", "Azure",
              "Docker", "Kubernetes", "SQL Server", "PostgreSQL", "MongoDB", "Redis",
              "REST APIs", "GraphQL", "Microservices", "CI/CD", "Jenkins", "GitHub Actions"
            ].map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
              >
                <Box
                  sx={{
                    py: 1.5,
                    px: 3,
                    bgcolor: 'white',
                    borderRadius: 10,
                    boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                    fontWeight: 500,
                  }}
                >
                  {tech}
                </Box>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
          >
            Benefits of Custom Software Development
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 6 
            }}
          >
            Discover how custom software can transform your business operations and create competitive advantages.
          </Typography>
        </motion.div>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <List>
              {[
                "Perfect alignment with your unique business processes",
                "Increased operational efficiency and productivity",
                "Improved data security and compliance with regulations",
                "Seamless integration with existing systems",
                "Enhanced scalability to support business growth"
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ListItem sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckCircleOutlineIcon sx={{ color: '#0c2340' }} />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                </motion.div>
              ))}
            </List>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <List>
              {[
                "Competitive advantage through unique capabilities",
                "Better user experience designed for your specific users",
                "Full ownership of the software and data",
                "Long-term cost savings compared to off-the-shelf solutions",
                "Ongoing support and evolution of your solution"
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ListItem sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckCircleOutlineIcon sx={{ color: '#0c2340' }} />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                </motion.div>
              ))}
            </List>
          </Box>
        </Box>
      </Container>

      {/* Call to Action Section */}
      <Box sx={{ bgcolor: '#0c2340', color: 'white', py: 10 }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ textAlign: 'center' }}
          >
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Ready to Transform Your Business with Custom Software?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9 }}>
              Let's discuss how our software development expertise can help you achieve your business goals.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              color="primary"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#0c2340',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              Contact Us Today
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default SoftwareDevelopment; 